import React from 'react';
import {
  Box,
  CircularProgress,
  IconButton,
  Popover,
  Typography,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import FlagRoundedIcon from '@mui/icons-material/FlagRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import ScienceRoundedIcon from '@mui/icons-material/ScienceRounded';
import { formatDate } from '@/utils/date-utils';
import { getValidAllergies, getValidFoodIntolerances, getValidPreferences } from '@/lib/clientPreferenceOptions';
import { getClientMedicalConditions } from '@/lib/api/clients';
import {
  formatActiveMedicalConditionsFromApi,
  getActiveMedicalConditionLabels,
} from '@/utils/client-medical-conditions';
import type { Client } from '@/types/client';
import { formatDisplayLabel } from '@/utils/display-labels';
import {
  getClientDailyCalorieGoalKcal,
  getClientGoalRows,
  type ClientGoalRow,
} from '@/clients/details/v2/components/clientDetailsV2.utils';
import { Tag } from '@/components/ui/tag';

export type ClientSummaryReadOnlyPopoverKind =
  | 'about'
  | 'healthGoal'
  | 'preferences'
  | 'allergies'
  | 'intolerances'
  | 'medical'
  | 'labReports'
  | 'clinicalTags'
  | 'clientTargets';

type MedicalConditionMetric = {
  label: string;
  value: string;
  isAlert?: boolean;
};

type MedicalConditionDetail = {
  name: string;
  autoimmuneSummary?: string;
  diagnosedOn: string;
  statusLabel: string;
  statusColor: string;
  metrics: MedicalConditionMetric[];
};

type LabReportAbnormalBiomarker = {
  biomarkerId: string;
  name: string;
  value: string;
  unit: string;
  status: string;
  testedDate: string | null;
};

type LatestLabReportAbnormalBiomarkers = {
  reportId: string;
  reportDate: string | null;
  high: LabReportAbnormalBiomarker[];
  low: LabReportAbnormalBiomarker[];
  borderline: LabReportAbnormalBiomarker[];
};

const AllergiesGlyph = ({ size = 16 }: { size?: number }) => (
  <Box
    component="svg"
    width={size}
    height={size}
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    sx={{ display: 'block' }}
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M13.3333 3.66732C13.3333 4.2196 12.8856 4.66732 12.3333 4.66732C12.2438 4.66732 12.1569 4.65554 12.0743 4.63345L11.6535 5.05422C11.6383 5.06948 11.6223 5.08354 11.6057 5.0964C12.0523 5.64641 12.3789 6.2973 12.5443 7.00806C12.5844 7.00317 12.6252 7.00065 12.6667 7.00065C13.219 7.00065 13.6667 7.44837 13.6667 8.00065C13.6667 8.55294 13.219 9.00065 12.6667 9.00065H12.5748C12.3947 9.89158 11.9622 10.6833 11.3498 11.3034L12.1075 12.0677C12.1901 12.0456 12.277 12.0339 12.3665 12.0339C12.9188 12.0339 13.3665 12.4816 13.3665 13.0339C13.3665 13.5861 12.9188 14.0339 12.3665 14.0339C11.8143 14.0339 11.3665 13.5861 11.3665 13.0339C11.3665 12.9443 11.3783 12.8574 11.4004 12.7748L10.6464 12.0209C10.6194 11.9938 10.5961 11.9644 10.5765 11.9333C10.2621 12.1368 9.92085 12.3028 9.55899 12.4251L9.41454 12.8339C9.20887 13.4158 8.65391 13.834 8 13.834C7.34609 13.834 6.79113 13.4158 6.58546 12.8339L6.42053 12.3672C6.0906 12.2432 5.77782 12.0834 5.48699 11.8925L4.56934 12.833C4.6321 12.9876 4.66667 13.1568 4.66667 13.334C4.66667 14.0704 4.06971 14.6673 3.33333 14.6673C2.59695 14.6673 2 14.0704 2 13.334C2 12.5976 2.59695 12.0007 3.33333 12.0007C3.5255 12.0007 3.70818 12.0413 3.87323 12.1145L4.71006 11.2568C4.17787 10.7193 3.77235 10.0562 3.54354 9.31751C3.47506 9.32835 3.40486 9.33398 3.33333 9.33398C2.59695 9.33398 2 8.73703 2 8.00065C2 7.26427 2.59695 6.66732 3.33333 6.66732C3.3907 6.66732 3.44723 6.67094 3.50269 6.67797C3.671 6.07417 3.95886 5.52164 4.33948 5.04716L3.8541 4.56178C3.69411 4.62973 3.51811 4.66732 3.33333 4.66732C2.59695 4.66732 2 4.07036 2 3.33398C2 2.5976 2.59695 2.00065 3.33333 2.00065C4.06971 2.00065 4.66667 2.5976 4.66667 3.33398C4.66667 3.51872 4.6291 3.69468 4.56118 3.85465L5.04659 4.34006C5.72938 3.79237 6.57378 3.43677 7.5 3.35304V3.2002C7.2011 3.0273 7 2.70413 7 2.33398C7 1.7817 7.44772 1.33398 8 1.33398C8.55228 1.33398 9 1.7817 9 2.33398C9 2.70413 8.7989 3.0273 8.5 3.2002V3.36909C9.40278 3.47978 10.2294 3.847 10.9042 4.39498C10.9171 4.37836 10.9312 4.36237 10.9464 4.34712L11.3672 3.92634C11.3451 3.84373 11.3333 3.7569 11.3333 3.66732C11.3333 3.11503 11.781 2.66732 12.3333 2.66732C12.8856 2.66732 13.3333 3.11503 13.3333 3.66732ZM7.83333 8.66732C7.83333 7.6548 8.65414 6.83398 9.66667 6.83398C10.6792 6.83398 11.5 7.6548 11.5 8.66732C11.5 9.67984 10.6792 10.5007 9.66667 10.5007C8.65414 10.5007 7.83333 9.67984 7.83333 8.66732ZM7 6.66732C7.55229 6.66732 8 6.2196 8 5.66732C8 5.62864 7.9978 5.59048 7.99353 5.55294C7.9569 5.23128 7.76767 4.95594 7.5 4.8011C7.35291 4.71601 7.18214 4.66732 7 4.66732C6.44772 4.66732 6 5.11503 6 5.66732C6 6.2196 6.44772 6.66732 7 6.66732ZM6 9.33398C6.36819 9.33398 6.66667 9.03551 6.66667 8.66732C6.66667 8.29913 6.36819 8.00065 6 8.00065C5.63181 8.00065 5.33333 8.29913 5.33333 8.66732C5.33333 9.03551 5.63181 9.33398 6 9.33398Z"
      fill="#20AEDC"
    />
    <path
      d="M8.83333 8.66732C8.83333 8.20708 9.20643 7.83398 9.66667 7.83398C10.1269 7.83398 10.5 8.20708 10.5 8.66732C10.5 9.12755 10.1269 9.50065 9.66667 9.50065C9.20643 9.50065 8.83333 9.12755 8.83333 8.66732Z"
      fill="#20AEDC"
    />
  </Box>
);

const MedicalConditionsGlyph = ({ size = 16 }: { size?: number }) => (
  <Box
    component="svg"
    width={size}
    height={size}
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    sx={{ display: 'block' }}
  >
    <path
      opacity="0.5"
      d="M2 6.66732C2 4.15316 2 2.89608 2.78105 2.11503C3.5621 1.33398 4.81918 1.33398 7.33333 1.33398H8.66667C11.1808 1.33398 12.4379 1.33398 13.219 2.11503C14 2.89608 14 4.15316 14 6.66732V9.33398C14 11.8481 14 13.1052 13.219 13.8863C12.4379 14.6673 11.1808 14.6673 8.66667 14.6673H7.33333C4.81918 14.6673 3.5621 14.6673 2.78105 13.8863C2 13.1052 2 11.8481 2 9.33398V6.66732Z"
      fill="#20AEDC"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M8.00003 3.5C8.27617 3.5 8.50003 3.72386 8.50003 4V4.83333H9.33335C9.6095 4.83333 9.83335 5.05719 9.83335 5.33333C9.83335 5.60948 9.6095 5.83333 9.33335 5.83333L8.50003 5.83333L8.50003 6.66667C8.50003 6.94281 8.27617 7.16667 8.00003 7.16667C7.72389 7.16667 7.50003 6.94281 7.50003 6.66667L7.50003 5.83333H6.66669C6.39054 5.83333 6.16669 5.60948 6.16669 5.33333C6.16669 5.05719 6.39054 4.83333 6.66669 4.83333H7.50003V4C7.50003 3.72386 7.72389 3.5 8.00003 3.5ZM4.83337 9.33333C4.83337 9.05719 5.05723 8.83333 5.33337 8.83333H10.6667C10.9428 8.83333 11.1667 9.05719 11.1667 9.33333C11.1667 9.60948 10.9428 9.83333 10.6667 9.83333H5.33337C5.05723 9.83333 4.83337 9.60948 4.83337 9.33333ZM5.50004 12C5.50004 11.7239 5.7239 11.5 6.00004 11.5H10C10.2762 11.5 10.5 11.7239 10.5 12C10.5 12.2761 10.2762 12.5 10 12.5H6.00004C5.7239 12.5 5.50004 12.2761 5.50004 12Z"
      fill="#20AEDC"
    />
  </Box>
);

const IntolerancesGlyph = ({ size = 16 }: { size?: number }) => (
  <Box
    component="svg"
    width={size}
    height={size}
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    sx={{ display: 'block' }}
  >
    <path
      opacity="0.5"
      d="M9.83539 4.75015C10.6164 5.5312 11.8828 5.5312 12.6638 4.75015C13.0543 4.35963 13.2496 3.84778 13.2496 3.33594C13.2496 2.82409 13.0543 2.31225 12.6638 1.92172C11.8828 1.14068 10.6164 1.14068 9.83539 1.92172C9.44487 2.31225 9.2496 2.82409 9.2496 3.33594C9.2496 3.84778 9.44487 4.35963 9.83539 4.75015Z"
      fill="#20AEDC"
    />
    <path
      opacity="0.5"
      d="M13.9339 11.59C14.9071 10.6168 14.9071 9.03898 13.9339 8.06581C12.9608 7.09265 11.383 7.09265 10.4098 8.06581L8.06036 10.4152C7.08719 11.3884 7.08719 12.9662 8.06036 13.9394C9.03353 14.9126 10.6113 14.9126 11.5845 13.9394L13.9339 11.59Z"
      fill="#20AEDC"
    />
  </Box>
);

const popoverPaperClientTargetsSx = {
  mt: 1.05,
  width: { xs: 'calc(100vw - 2.25rem)', sm: 'min(100vw - 4.5rem, 760px)' },
  maxWidth: '760px',
  borderRadius: '10px',
  overflow: 'visible',
  border: '1px solid #d5d9e0',
  boxShadow: '0 18px 36px rgba(15, 23, 42, 0.22)',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: -8,
    left: 34,
    width: 16,
    height: 16,
    bgcolor: '#fff',
    borderTop: '1px solid #d5d9e0',
    borderLeft: '1px solid #d5d9e0',
    transform: 'rotate(45deg)',
  },
} as const;

const readOnlyGoalValueSx = {
  minWidth: 70,
  height: 30,
  borderRadius: '8px',
  border: '1px solid #CBD5E1',
  display: 'grid',
  placeItems: 'center',
  color: '#20BFB8',
  fontSize: '0.85rem',
  fontWeight: 800,
  fontVariantNumeric: 'tabular-nums',
} as const;

function formatReadOnlyGoalValue(value: string | undefined): string {
  const trimmed = String(value ?? '').trim();
  return trimmed || '--';
}

function renderReadOnlyClientGoalRow(row: ClientGoalRow) {
  const hasKcal = row.kcal != null;

  return (
    <Box
      key={row.nutrient}
      sx={{
        display: 'grid',
        gridTemplateColumns: '1.2fr 1fr 1.35fr 2fr',
        alignItems: 'center',
        borderBottom: '1px solid #F2F4F7',
        minHeight: 44,
      }}
    >
      <Typography sx={{ px: 1.35, fontFamily: 'Inter, sans-serif', fontSize: '0.78rem', fontWeight: 700, color: '#1f2937' }}>
        {row.nutrient}
      </Typography>
      <Typography sx={{ px: 1.35, fontFamily: 'Inter, sans-serif', fontSize: '0.76rem', color: '#98A2B3' }}>
        {row.percentage}
      </Typography>
      <Typography sx={{ px: 1.35, fontFamily: 'Inter, sans-serif', fontSize: '0.76rem', color: '#475569' }}>
        {row.referenceRange}
      </Typography>
      <Box sx={{ px: 1.35, display: 'flex', alignItems: 'center', gap: 0.45 }}>
        <Box sx={readOnlyGoalValueSx}>{formatReadOnlyGoalValue(row.grams)}</Box>
        <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.75rem', color: '#98A2B3' }}>
          {row.unit || 'g'}
        </Typography>
        {hasKcal ? (
          <>
            <Box sx={{ ...readOnlyGoalValueSx, minWidth: 78 }}>{formatReadOnlyGoalValue(row.kcal)}</Box>
            <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.75rem', color: '#98A2B3' }}>kcal</Typography>
          </>
        ) : null}
      </Box>
    </Box>
  );
}

const popoverPaperAboutSx = {
  mt: 1.05,
  width: { xs: 'calc(100vw - 2.25rem)', sm: 'min(100vw - 4.5rem, 500px)' },
  maxWidth: '500px',
  borderRadius: '10px',
  overflow: 'visible',
  border: '1px solid #d5d9e0',
  boxShadow: '0 18px 36px rgba(15, 23, 42, 0.22)',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: -8,
    left: 34,
    width: 16,
    height: 16,
    bgcolor: '#f2f4f7',
    borderTop: '1px solid #d5d9e0',
    borderLeft: '1px solid #d5d9e0',
    transform: 'rotate(45deg)',
  },
} as const;

const popoverPaperDefaultSx = {
  mt: 1.05,
  width: { xs: 'calc(100vw - 3.25rem)', sm: 'min(100vw - 5rem, 440px)' },
  maxWidth: '440px',
  borderRadius: '10px',
  overflow: 'visible',
  border: '1px solid #d5d9e0',
  boxShadow: '0 18px 36px rgba(15, 23, 42, 0.22)',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: -8,
    left: 34,
    width: 16,
    height: 16,
    bgcolor: '#f2f4f7',
    borderTop: '1px solid #d5d9e0',
    borderLeft: '1px solid #d5d9e0',
    transform: 'rotate(45deg)',
  },
} as const;

const popoverPaperMedicalSx = {
  mt: 1.05,
  width: { xs: 'calc(100vw - 3.25rem)', sm: 'min(100vw - 5rem, 440px)' },
  maxWidth: '440px',
  borderRadius: '10px',
  overflow: 'visible',
  border: '1px solid #d5d9e0',
  boxShadow: '0 18px 36px rgba(15, 23, 42, 0.22)',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: -8,
    right: 24,
    width: 16,
    height: 16,
    bgcolor: '#f2f4f7',
    borderTop: '1px solid #d5d9e0',
    borderLeft: '1px solid #d5d9e0',
    transform: 'rotate(45deg)',
  },
} as const;

/** Cap list height so a few biomarker cards show; remainder scrolls inside the popover. */
const labReportBiomarkerListScrollSx = {
  display: 'flex',
  flexDirection: 'column',
  gap: 0.72,
  maxHeight: 240,
  overflowY: 'auto',
  pr: 0.5,
  WebkitOverflowScrolling: 'touch',
} as const;

function getAgeFromDateOfBirth(dateOfBirth?: string | null): number | null {
  if (!dateOfBirth) return null;
  const dob = new Date(dateOfBirth);
  if (Number.isNaN(dob.getTime())) return null;

  const now = new Date();
  let age = now.getFullYear() - dob.getFullYear();
  const monthDiff = now.getMonth() - dob.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < dob.getDate())) {
    age -= 1;
  }

  return age >= 0 ? age : null;
}

function readFirstString(record: Record<string, unknown>, keys: string[]): string {
  for (const key of keys) {
    const value = record[key];
    if (typeof value !== 'string') continue;
    const trimmed = value.trim();
    if (trimmed) return trimmed;
  }
  return '';
}

function formatPhoneDisplay(client: Client): string {
  const record = client as unknown as Record<string, unknown>;
  const code = readFirstString(record, ['phoneCountryCode', 'phone_country_code', 'countryCode', 'dialCode']);
  const number = readFirstString(record, [
    'phone',
    'phoneNumber',
    'phone_number',
    'mobile',
    'mobileNumber',
    'mobile_number',
    'contactNumber',
    'contact_number',
  ]);
  if (!number) return '-';

  const normalizedCode = code && code.startsWith('+') ? code : code ? `+${code}` : '';
  const normalizedNumber = number.replace(/\s+/g, ' ').trim();
  const codeDigits = normalizedCode.replace(/\D/g, '');
  const numberDigits = normalizedNumber.replace(/\D/g, '');

  if (normalizedCode && codeDigits && numberDigits.startsWith(codeDigits)) {
    return normalizedNumber.startsWith('+') ? normalizedNumber : `+${normalizedNumber}`;
  }

  return [normalizedCode, normalizedNumber].filter(Boolean).join(' ');
}

function buildAboutFieldsFromClient(client: Client | null): Array<{ label: string; value: string }> {
  if (!client) {
    return [{ label: 'STATUS', value: 'Client details not loaded.' }];
  }
  const fullName =
    String(client.fullName ?? '').trim()
    || [client.firstName, client.lastName].filter((p) => String(p ?? '').trim()).join(' ').trim()
    || 'Not specified';
  const age = getAgeFromDateOfBirth(client.dateOfBirth);
  const dob = client.dateOfBirth
    ? `${formatDate(client.dateOfBirth, 'dd MMM yyyy', '-')}${age != null ? ` (${age})` : ''}`
    : '-';
  const gender = client.gender ? formatDisplayLabel(client.gender) : '-';
  const sexAtBirth = client.sexAtBirth ? formatDisplayLabel(client.sexAtBirth) : '-';
  const phone = formatPhoneDisplay(client);
  const email = String(client.email ?? '').trim() || '-';
  const shouldShowSexAtBirth =
    sexAtBirth !== '-' && sexAtBirth.trim().toLowerCase() !== gender.trim().toLowerCase();

  const fields: Array<{ label: string; value: string }> = [
    { label: 'FULL NAME', value: fullName },
    { label: 'DATE OF BIRTH', value: dob },
    { label: 'GENDER', value: gender },
  ];
  if (shouldShowSexAtBirth) fields.push({ label: 'SEX AT BIRTH', value: sexAtBirth });
  const healthGoal = String(client.healthGoal ?? '').trim();
  fields.push(
    { label: 'PHONE', value: phone },
    { label: 'EMAIL', value: email },
    { label: 'HEALTH GOAL', value: healthGoal || 'Not set' },
  );
  return fields;
}

function resolveAllergyLabel(value: string): string {
  return getValidAllergies().find((a) => a.value.toLowerCase() === value.toLowerCase())?.name || formatDisplayLabel(value);
}

function resolveIntoleranceLabel(value: string): string {
  return getValidFoodIntolerances().find((i) => i.value.toLowerCase() === value.toLowerCase())?.name || formatDisplayLabel(value);
}

function resolvePreferenceLabel(value: string): string {
  return getValidPreferences().find((i) => i.value.toLowerCase() === value.toLowerCase())?.name || formatDisplayLabel(value);
}

function formatMedicalFromApi(raw: unknown): MedicalConditionDetail[] {
  const data = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const fromConditions = Array.isArray(data.conditions) ? data.conditions : [];
  const fromDetails = Array.isArray(data.medicalConditionDetails) ? data.medicalConditionDetails : [];
  const list = fromConditions.length > 0 ? fromConditions : fromDetails;
  return formatActiveMedicalConditionsFromApi(list);
}

function medicalFallbackFromClient(client: Client | null | undefined): MedicalConditionDetail[] {
  const activeLabels = getActiveMedicalConditionLabels(client);
  if (activeLabels.length === 0) return [];
  return activeLabels.map((name) => ({
    name,
    diagnosedOn: '',
    statusLabel: 'Active',
    statusColor: '#EF4444',
    metrics: [],
  }));
}

function formatLabReportStatus(status: string): string {
  const normalized = status.trim().toLowerCase();
  if (!normalized) return 'Abnormal';
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}

function formatLabReportDate(value: string | null, fallback = 'Unknown date'): string {
  if (!value) return fallback;
  return formatDate(value, 'dd MMM yyyy', value);
}

function parseLatestLabReportAbnormalBiomarkers(raw: unknown): LatestLabReportAbnormalBiomarkers | null {
  const payload = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const section = payload.latestLabReportAbnormalBiomarkers;
  if (!section || typeof section !== 'object' || Array.isArray(section)) return null;

  const latest = section as Record<string, unknown>;
  const reportId = String(latest.reportId ?? '').trim();
  const reportDate = String(latest.reportDate ?? '').trim() || null;

  const normalizeBiomarker = (value: unknown): LabReportAbnormalBiomarker | null => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
    const biomarker = value as Record<string, unknown>;

    const biomarkerId = String(biomarker.biomarkerId ?? '').trim();
    const name = String(biomarker.name ?? '').trim() || biomarkerId;
    if (!name) return null;

    const valueRaw = biomarker.value;
    const displayValue =
      typeof valueRaw === 'number' && Number.isFinite(valueRaw)
        ? String(valueRaw)
        : String(valueRaw ?? '').trim() || '—';

    return {
      biomarkerId,
      name,
      value: displayValue,
      unit: String(biomarker.unit ?? '').trim(),
      status: String(biomarker.status ?? '').trim(),
      testedDate: String(biomarker.testedDate ?? '').trim() || null,
    };
  };

  const normalizeList = (value: unknown): LabReportAbnormalBiomarker[] => {
    if (!Array.isArray(value)) return [];
    return value
      .map((item) => normalizeBiomarker(item))
      .filter((item): item is LabReportAbnormalBiomarker => Boolean(item));
  };

  const high = normalizeList(latest.high);
  const low = normalizeList(latest.low);
  const borderline = normalizeList(latest.borderline);

  if (!reportId && !reportDate && high.length === 0 && low.length === 0 && borderline.length === 0) {
    return null;
  }

  return {
    reportId,
    reportDate,
    high,
    low,
    borderline,
  };
}

export type ClientSummaryReadOnlyPopoversProps = {
  client: Client | null;
  clientId: string;
  clinicalTags?: string[];
  openKind: ClientSummaryReadOnlyPopoverKind | null;
  anchorEl: HTMLElement | null;
  onClose: () => void;
};

export const ClientSummaryReadOnlyPopovers: React.FC<ClientSummaryReadOnlyPopoversProps> = ({
  client,
  clientId,
  clinicalTags = [],
  openKind,
  anchorEl,
  onClose,
}) => {
  const open = Boolean(openKind && anchorEl);
  const [medicalDetails, setMedicalDetails] = React.useState<MedicalConditionDetail[]>([]);
  const [latestLabReportAbnormalBiomarkers, setLatestLabReportAbnormalBiomarkers] =
    React.useState<LatestLabReportAbnormalBiomarkers | null>(null);
  const [medicalLoading, setMedicalLoading] = React.useState(false);
  const [medicalFetched, setMedicalFetched] = React.useState(false);
  const medicalRequestInFlightRef = React.useRef(false);

  React.useEffect(() => {
    if ((openKind !== 'medical' && openKind !== 'labReports') || !clientId.trim()) {
      return;
    }
    if (medicalFetched || medicalRequestInFlightRef.current) return;

    let cancelled = false;
    (async () => {
      medicalRequestInFlightRef.current = true;
      setMedicalLoading(true);
      try {
        const res = await getClientMedicalConditions(clientId);
        if (cancelled) return;
        const formatted = formatMedicalFromApi(res);
        setMedicalDetails(formatted.length > 0 ? formatted : medicalFallbackFromClient(client));
        setLatestLabReportAbnormalBiomarkers(parseLatestLabReportAbnormalBiomarkers(res));
        setMedicalFetched(true);
      } catch {
        if (!cancelled) {
          setMedicalDetails(medicalFallbackFromClient(client));
          setLatestLabReportAbnormalBiomarkers(null);
          setMedicalFetched(true);
        }
      } finally {
        medicalRequestInFlightRef.current = false;
        if (!cancelled) setMedicalLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [openKind, clientId, client?.medicalConditions, medicalFetched]);

  React.useEffect(() => {
    if (openKind !== 'medical' && openKind !== 'labReports') {
      setMedicalFetched(false);
      setMedicalDetails([]);
      setLatestLabReportAbnormalBiomarkers(null);
      medicalRequestInFlightRef.current = false;
    }
  }, [openKind]);

  const aboutFields = React.useMemo(() => buildAboutFieldsFromClient(client), [client]);
  const healthGoalDisplay = React.useMemo(() => {
    const value = String(client?.healthGoal ?? '').trim();
    return value || 'Not set';
  }, [client?.healthGoal]);
  const allergyValues = React.useMemo(
    () => (Array.isArray(client?.allergies) ? client!.allergies.map((a) => String(a ?? '').trim()).filter(Boolean) : []),
    [client],
  );
  const intoleranceValues = React.useMemo(
    () =>
      Array.isArray(client?.foodIntolerances)
        ? client!.foodIntolerances.map((a) => String(a ?? '').trim()).filter(Boolean)
        : [],
    [client],
  );
  const preferenceValues = React.useMemo(
    () =>
      Array.isArray(client?.dietaryPreferences)
        ? client!.dietaryPreferences.map((a) => String(a ?? '').trim()).filter(Boolean)
        : [],
    [client],
  );
  const clinicalTagValues = React.useMemo(
    () => clinicalTags.map((tag) => formatDisplayLabel(tag)).filter(Boolean),
    [clinicalTags],
  );
  const clientGoalRows = React.useMemo(
    () => (client ? getClientGoalRows(client) : []),
    [client],
  );
  const dailyCalorieGoalKcal = React.useMemo(
    () => (client ? getClientDailyCalorieGoalKcal(client) : null),
    [client],
  );
  const referenceCalorieKcal = React.useMemo(() => {
    if (!client) return null;
    const eer = Number(client.eerCalories ?? client.eer);
    return Number.isFinite(eer) && eer > 0 ? Math.round(eer) : null;
  }, [client]);
  const highBiomarkers = latestLabReportAbnormalBiomarkers?.high ?? [];
  const lowBiomarkers =
    (latestLabReportAbnormalBiomarkers?.low ?? []).concat(latestLabReportAbnormalBiomarkers?.borderline ?? []);
  const totalAbnormalBiomarkers = highBiomarkers.length + lowBiomarkers.length;

  const useRightAnchor = openKind === 'medical' || openKind === 'labReports';
  const paperSx =
    openKind === 'clientTargets'
      ? popoverPaperClientTargetsSx
      : openKind === 'about'
        ? popoverPaperAboutSx
        : useRightAnchor
          ? popoverPaperMedicalSx
          : popoverPaperDefaultSx;
  const anchorHorizontal = useRightAnchor ? 'right' : 'left';
  const transformHorizontal = useRightAnchor ? 'right' : 'left';

  const headerRow = (icon: React.ReactNode, title: string) => (
    <Box
      sx={{
        px: { xs: 1.75, sm: 2.5 },
        py: 1.45,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 1,
        bgcolor: '#f2f4f7',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {icon}
        <Typography
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontSize: '0.95rem',
            fontWeight: 700,
            color: '#1f2937',
          }}
        >
          {title}
        </Typography>
      </Box>
      <IconButton
        aria-label={`Close ${title}`}
        onClick={onClose}
        sx={{
          width: 32,
          height: 32,
          bgcolor: '#20AEDC',
          color: '#fff',
          '&:hover': { bgcolor: '#1297c4' },
        }}
      >
        <CloseRoundedIcon sx={{ fontSize: 15 }} />
      </IconButton>
    </Box>
  );

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: anchorHorizontal }}
      transformOrigin={{ vertical: 'top', horizontal: transformHorizontal }}
      disableAutoFocus
      disableRestoreFocus
      PaperProps={{
        sx: paperSx,
      }}
    >
      {openKind === 'about' ? (
        <>
          {headerRow(<PersonRoundedIcon sx={{ color: '#20AEDC', fontSize: 19 }} />, 'About')}
          <Box sx={{ px: { xs: 1.75, sm: 2.5 }, py: { xs: 1.8, sm: 2 }, bgcolor: '#fff' }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                gap: { xs: 1.6, sm: 2 },
                columnGap: { sm: 3.5 },
                rowGap: { sm: 3 },
              }}
            >
              {aboutFields.map((field) => (
                <Box key={field.label} sx={{ minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                  <Typography
                    sx={{
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '0.75rem',
                      letterSpacing: '0.05em',
                      textTransform: 'uppercase',
                      color: '#98A2B3',
                    }}
                  >
                    {field.label}
                  </Typography>
                  <Typography
                    sx={{
                      mt: 0.4,
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '0.88rem',
                      fontWeight: 500,
                      lineHeight: 1.3,
                      color: '#344054',
                      overflowWrap: 'anywhere',
                    }}
                  >
                    {field.value}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </>
      ) : null}

      {openKind === 'healthGoal' ? (
        <>
          {headerRow(<FlagRoundedIcon sx={{ color: '#20AEDC', fontSize: 19 }} />, 'Health Goal')}
          <Box sx={{ px: { xs: 1.75, sm: 2.5 }, py: { xs: 1.8, sm: 2 }, bgcolor: '#fff' }}>
            <Typography
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontSize: '0.75rem',
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
                color: '#98A2B3',
              }}
            >
              Client health goal
            </Typography>
            <Typography
              sx={{
                mt: 0.4,
                fontFamily: 'Inter, sans-serif',
                fontSize: '0.88rem',
                fontWeight: 500,
                lineHeight: 1.45,
                color: '#344054',
                overflowWrap: 'anywhere',
                whiteSpace: 'pre-wrap',
              }}
            >
              {healthGoalDisplay}
            </Typography>
          </Box>
        </>
      ) : null}

      {openKind === 'allergies' ? (
        <>
          {headerRow(
            <Box
              sx={{
                width: 26,
                height: 26,
                borderRadius: '8px',
                bgcolor: '#e8f6fc',
                display: 'grid',
                placeItems: 'center',
              }}
            >
              <AllergiesGlyph size={16} />
            </Box>,
            'Allergies',
          )}
          <Box sx={{ px: { xs: 1.75, sm: 2.5 }, py: { xs: 1.9, sm: 2 }, bgcolor: '#fff' }}>
            <Typography
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontSize: '0.75rem',
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
                color: '#98A2B3',
                mb: 1,
              }}
            >
              Current allergies
            </Typography>
            {allergyValues.length === 0 ? (
              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.82rem', color: '#98A2B3' }}>
                No allergies recorded.
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {allergyValues.map((value) => (
                  <Tag
                    key={value}
                    variant="neutral"
                    size="sm"
                    maxWidth="100%"
                    className="border-[#EAECF0] bg-[#F8FAFC] font-[Inter,sans-serif] font-medium"
                  >
                    {resolveAllergyLabel(value)}
                  </Tag>
                ))}
              </Box>
            )}
          </Box>
        </>
      ) : null}

      {openKind === 'preferences' ? (
        <>
          {headerRow(
            <Box
              sx={{
                width: 26,
                height: 26,
                borderRadius: '8px',
                bgcolor: '#e8f6fc',
                display: 'grid',
                placeItems: 'center',
              }}
            >
              <FlagRoundedIcon sx={{ color: '#20AEDC', fontSize: 18 }} />
            </Box>,
            'Dietary Preferences',
          )}
          <Box sx={{ px: { xs: 1.75, sm: 2.5 }, py: { xs: 1.9, sm: 2 }, bgcolor: '#fff' }}>
            <Typography
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontSize: '0.75rem',
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
                color: '#98A2B3',
                mb: 1,
              }}
            >
              Current dietary preferences
            </Typography>
            {preferenceValues.length === 0 ? (
              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.82rem', color: '#98A2B3' }}>
                No dietary preferences recorded.
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {preferenceValues.map((value) => (
                  <Tag
                    key={value}
                    variant="neutral"
                    size="sm"
                    maxWidth="100%"
                    className="border-[#EAECF0] bg-[#F8FAFC] font-[Inter,sans-serif] font-medium"
                  >
                    {resolvePreferenceLabel(value)}
                  </Tag>
                ))}
              </Box>
            )}
          </Box>
        </>
      ) : null}

      {openKind === 'intolerances' ? (
        <>
          {headerRow(
            <Box
              sx={{
                width: 26,
                height: 26,
                borderRadius: '8px',
                bgcolor: '#e8f6fc',
                display: 'grid',
                placeItems: 'center',
              }}
            >
              <IntolerancesGlyph size={16} />
            </Box>,
            'Intolerances',
          )}
          <Box sx={{ px: { xs: 1.75, sm: 2.5 }, py: { xs: 1.9, sm: 2 }, bgcolor: '#fff' }}>
            <Typography
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontSize: '0.75rem',
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
                color: '#98A2B3',
                mb: 1,
              }}
            >
              Current intolerances
            </Typography>
            {intoleranceValues.length === 0 ? (
              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.82rem', color: '#98A2B3' }}>
                No intolerances recorded.
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {intoleranceValues.map((value) => (
                  <Tag
                    key={value}
                    variant="neutral"
                    size="sm"
                    maxWidth="100%"
                    className="border-[#EAECF0] bg-[#F8FAFC] font-[Inter,sans-serif] font-medium"
                  >
                    {resolveIntoleranceLabel(value)}
                  </Tag>
                ))}
              </Box>
            )}
          </Box>
        </>
      ) : null}

      {openKind === 'clinicalTags' ? (
        <>
          {headerRow(
            <Box
              sx={{
                width: 26,
                height: 26,
                borderRadius: '8px',
                bgcolor: '#e8f6fc',
                display: 'grid',
                placeItems: 'center',
              }}
            >
              <ScienceRoundedIcon sx={{ color: '#20AEDC', fontSize: 18 }} />
            </Box>,
            'Clinical Tags',
          )}
          <Box sx={{ px: { xs: 1.75, sm: 2.5 }, py: { xs: 1.9, sm: 2 }, bgcolor: '#fff' }}>
            <Typography
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontSize: '0.75rem',
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
                color: '#98A2B3',
                mb: 1,
              }}
            >
              Current clinical tags
            </Typography>
            {clinicalTagValues.length === 0 ? (
              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.82rem', color: '#98A2B3' }}>
                No clinical tags recorded.
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {clinicalTagValues.map((value) => (
                  <Tag
                    key={value}
                    variant="neutral"
                    size="sm"
                    maxWidth="100%"
                    className="border-[#EAECF0] bg-[#F8FAFC] font-[Inter,sans-serif] font-medium"
                  >
                    {value}
                  </Tag>
                ))}
              </Box>
            )}
          </Box>
        </>
      ) : null}

      {openKind === 'medical' ? (
        <>
          {headerRow(
            <Box
              sx={{
                width: 26,
                height: 26,
                borderRadius: '8px',
                bgcolor: '#e8f6fc',
                display: 'grid',
                placeItems: 'center',
              }}
            >
              <MedicalConditionsGlyph size={16} />
            </Box>,
            'Medical Conditions',
          )}
          <Box sx={{ px: { xs: 1.75, sm: 2.5 }, py: { xs: 1.5, sm: 1.7 }, bgcolor: '#fff' }}>
            {medicalLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={24} sx={{ color: '#20AEDC' }} />
              </Box>
            ) : medicalDetails.length ? (
              medicalDetails.map((condition, index) => (
                <Box
                  key={`${condition.name}-${index}`}
                  sx={{
                    py: 1.35,
                    borderBottom: index === medicalDetails.length - 1 ? 'none' : '1px solid #EAECF0',
                  }}
                >
                  <Box>
                    <Typography
                      sx={{
                        fontFamily: 'Inter, sans-serif',
                        fontSize: '0.95rem',
                        fontWeight: 700,
                        color: '#1F2937',
                        overflowWrap: 'anywhere',
                        lineHeight: 1.2,
                      }}
                    >
                      {condition.name}
                    </Typography>
                    {condition.autoimmuneSummary ? (
                      <Typography
                        sx={{
                          mt: 0.3,
                          fontFamily: 'Inter, sans-serif',
                          fontSize: '0.8rem',
                          color: '#667085',
                          lineHeight: 1.2,
                        }}
                      >
                        {condition.autoimmuneSummary}
                      </Typography>
                    ) : null}
                  </Box>
                  {condition.diagnosedOn ? (
                    <Typography
                      sx={{
                        mt: 0.18,
                        fontFamily: 'Inter, sans-serif',
                        fontSize: '0.75rem',
                        color: '#98A2B3',
                      }}
                    >
                      {condition.diagnosedOn}
                    </Typography>
                  ) : null}
                  {condition.metrics.length > 0 ? (
                    <Box
                      sx={{
                        mt: 0.75,
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                        gap: 0.7,
                      }}
                    >
                      {condition.metrics.slice(0, 2).map((metric) => (
                        <Box
                          key={`${condition.name}-${metric.label}`}
                          sx={{
                            borderRadius: '12px',
                            border: metric.isAlert ? '1px solid #FECACA' : '1px solid #EAECF0',
                            bgcolor: metric.isAlert ? '#FEF2F2' : '#F8FAFC',
                            px: 0.9,
                            py: 0.68,
                          }}
                        >
                          <Typography
                            sx={{
                              fontFamily: 'Inter, sans-serif',
                              fontSize: '0.75rem',
                              textTransform: 'uppercase',
                              letterSpacing: '0.03em',
                              color: '#98A2B3',
                            }}
                          >
                            {metric.label}
                          </Typography>
                          <Typography
                            sx={{
                              mt: 0.2,
                              fontFamily: 'Inter, sans-serif',
                              fontSize: '0.88rem',
                              fontWeight: 600,
                              color: metric.isAlert ? '#EF4444' : '#344054',
                            }}
                          >
                            {metric.value}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  ) : null}
                </Box>
              ))
            ) : (
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '0.82rem',
                  color: '#98A2B3',
                  textAlign: 'center',
                  py: 1.6,
                }}
              >
                No medical conditions available
              </Typography>
            )}
          </Box>
        </>
      ) : null}

      {openKind === 'labReports' ? (
        <>
          {headerRow(
            <Box
              sx={{
                width: 26,
                height: 26,
                borderRadius: '8px',
                bgcolor: '#e8f6fc',
                display: 'grid',
                placeItems: 'center',
              }}
            >
              <ScienceRoundedIcon sx={{ color: '#20AEDC', fontSize: 18 }} />
            </Box>,
            'Lab Reports',
          )}
          <Box sx={{ px: { xs: 1.75, sm: 2.5 }, py: { xs: 1.5, sm: 1.7 }, bgcolor: '#fff' }}>
            {medicalLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={24} sx={{ color: '#20AEDC' }} />
              </Box>
            ) : totalAbnormalBiomarkers > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.1 }}>
                <Box>
                  <Typography
                    sx={{
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '0.75rem',
                      color: '#98A2B3',
                    }}
                  >
                    Latest report: {formatLabReportDate(latestLabReportAbnormalBiomarkers?.reportDate ?? null)}
                  </Typography>
                  <Box sx={{ mt: 0.75, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                    <Tag variant="danger" size="sm" className="font-[Inter,sans-serif] font-semibold text-[#B91C1C]">
                      {`${highBiomarkers.length} High`}
                    </Tag>
                    <Tag variant="warning" size="sm" className="font-[Inter,sans-serif] font-semibold text-[#B45309]">
                      {`${lowBiomarkers.length} Low`}
                    </Tag>
                  </Box>
                </Box>

                {highBiomarkers.length > 0 ? (
                  <Box>
                    <Typography
                      sx={{
                        mb: 0.65,
                        fontFamily: 'Inter, sans-serif',
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                        color: '#98A2B3',
                      }}
                    >
                      High Biomarkers
                    </Typography>
                    <Box sx={labReportBiomarkerListScrollSx}>
                      {highBiomarkers.map((biomarker, index) => (
                        <Box
                          key={`${biomarker.biomarkerId || biomarker.name}-${index}`}
                          sx={{
                            borderRadius: '12px',
                            border: '1px solid #FECACA',
                            bgcolor: '#FEF2F2',
                            px: 1,
                            py: 0.84,
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography
                                sx={{
                                  fontFamily: 'Inter, sans-serif',
                                  fontSize: '0.9rem',
                                  fontWeight: 700,
                                  color: '#1F2937',
                                  lineHeight: 1.2,
                                }}
                              >
                                {biomarker.name}
                              </Typography>
                              <Typography
                                sx={{
                                  mt: 0.18,
                                  fontFamily: 'Inter, sans-serif',
                                  fontSize: '0.75rem',
                                  color: '#98A2B3',
                                }}
                              >
                                Tested {formatLabReportDate(biomarker.testedDate)}
                              </Typography>
                            </Box>
                            <Tag variant="danger" size="sm" className="font-[Inter,sans-serif] font-semibold text-[#B91C1C]">
                              {formatLabReportStatus(biomarker.status)}
                            </Tag>
                          </Box>
                          <Typography
                            sx={{
                              mt: 0.45,
                              fontFamily: 'Inter, sans-serif',
                              fontSize: '0.88rem',
                              fontWeight: 600,
                              color: '#B91C1C',
                            }}
                          >
                            {biomarker.value}
                            {biomarker.unit ? ` ${biomarker.unit}` : ''}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                ) : null}

                {lowBiomarkers.length > 0 ? (
                  <Box>
                    <Typography
                      sx={{
                        mb: 0.65,
                        fontFamily: 'Inter, sans-serif',
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                        color: '#98A2B3',
                      }}
                    >
                      Low Biomarkers
                    </Typography>
                    <Box sx={labReportBiomarkerListScrollSx}>
                      {lowBiomarkers.map((biomarker, index) => (
                        <Box
                          key={`${biomarker.biomarkerId || biomarker.name}-${index}`}
                          sx={{
                            borderRadius: '12px',
                            border: '1px solid #FDE68A',
                            bgcolor: '#FFFBEB',
                            px: 1,
                            py: 0.84,
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography
                                sx={{
                                  fontFamily: 'Inter, sans-serif',
                                  fontSize: '0.9rem',
                                  fontWeight: 700,
                                  color: '#1F2937',
                                  lineHeight: 1.2,
                                }}
                              >
                                {biomarker.name}
                              </Typography>
                              <Typography
                                sx={{
                                  mt: 0.18,
                                  fontFamily: 'Inter, sans-serif',
                                  fontSize: '0.75rem',
                                  color: '#98A2B3',
                                }}
                              >
                                Tested {formatLabReportDate(biomarker.testedDate)}
                              </Typography>
                            </Box>
                            <Tag variant="warning" size="sm" className="font-[Inter,sans-serif] font-semibold text-[#B45309]">
                              {formatLabReportStatus(biomarker.status)}
                            </Tag>
                          </Box>
                          <Typography
                            sx={{
                              mt: 0.45,
                              fontFamily: 'Inter, sans-serif',
                              fontSize: '0.88rem',
                              fontWeight: 600,
                              color: '#B45309',
                            }}
                          >
                            {biomarker.value}
                            {biomarker.unit ? ` ${biomarker.unit}` : ''}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                ) : null}
              </Box>
            ) : (
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '0.82rem',
                  color: '#98A2B3',
                  textAlign: 'center',
                  py: 1.6,
                }}
              >
                No abnormal biomarkers found in the latest lab report
              </Typography>
            )}
          </Box>
        </>
      ) : null}

      {openKind === 'clientTargets' ? (
        <>
          {headerRow(<FlagRoundedIcon sx={{ color: '#20AEDC', fontSize: 19 }} />, 'Client Targets')}
          <Box sx={{ px: 1.7, py: 1.7, bgcolor: '#fff' }}>
            {!client ? (
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '0.82rem',
                  color: '#98A2B3',
                  textAlign: 'center',
                  py: 1.6,
                }}
              >
                Client targets not available
              </Typography>
            ) : (
              <>
                <Box
                  sx={{
                    borderRadius: '9px',
                    bgcolor: '#eaf6ff',
                    px: 1.25,
                    py: 0.95,
                    display: 'grid',
                    gridTemplateColumns: 'minmax(0, 1fr) auto',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <Box>
                    <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.82rem', fontWeight: 700, color: '#334155' }}>
                      Daily Calorie Goals
                    </Typography>
                    <Typography sx={{ mt: 0.15, fontFamily: 'Inter, sans-serif', fontSize: '0.75rem', color: '#98A2B3' }}>
                      Reference {referenceCalorieKcal != null ? `${referenceCalorieKcal.toLocaleString()} kcal` : 'Not set'}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      minWidth: 132,
                      height: 34,
                      borderRadius: '9px',
                      bgcolor: '#fff',
                      display: 'grid',
                      placeItems: 'center',
                      color: '#20AEDC',
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '0.98rem',
                      fontWeight: 800,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {dailyCalorieGoalKcal != null ? dailyCalorieGoalKcal.toLocaleString() : '--'}
                  </Box>
                </Box>

                <Box sx={{ mt: 1.3, border: '1px solid #EAECF0', borderRadius: '10px', overflow: 'hidden' }}>
                  <Typography sx={{ px: 1.55, py: 1, fontFamily: 'Inter, sans-serif', fontSize: '0.9rem', fontWeight: 800, color: '#1f2937' }}>
                    Macronutrients
                  </Typography>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '1.2fr 1fr 1.35fr 2fr',
                      borderTop: '1px solid #EAECF0',
                      borderBottom: '1px solid #EAECF0',
                      bgcolor: '#fff',
                    }}
                  >
                    {['Nutrient', 'Percentage', 'Clinical Limit / Reference', 'Client Goal'].map((header) => (
                      <Typography
                        key={header}
                        sx={{
                          px: 1.35,
                          py: 0.65,
                          fontFamily: 'Inter, sans-serif',
                          fontSize: '0.75rem',
                          fontWeight: 800,
                          letterSpacing: '0.08em',
                          textTransform: 'uppercase',
                          color: '#98A2B3',
                        }}
                      >
                        {header}
                      </Typography>
                    ))}
                  </Box>
                  {clientGoalRows.length > 0 ? (
                    clientGoalRows.map((row) => renderReadOnlyClientGoalRow(row))
                  ) : (
                    <Typography
                      sx={{
                        px: 1.35,
                        py: 1.4,
                        fontFamily: 'Inter, sans-serif',
                        fontSize: '0.82rem',
                        color: '#98A2B3',
                        textAlign: 'center',
                      }}
                    >
                      No macronutrient targets set
                    </Typography>
                  )}
                </Box>
              </>
            )}
          </Box>
        </>
      ) : null}
    </Popover>
  );
};
